import type { MaybeRef } from '@vueuse/core'
import type {
  EventRegistrationsQuery,
  RegistrationStatus,
} from '@/gql/graphql.ts'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { graphqlRequest } from '@/service/graphqlClient.ts'
import { computed, unref } from 'vue'
import { EventRegistrationsDocument } from '@/gql/graphql.ts'
import { QUERY_KEY_ILT_REGISTRATIONS } from '@/queries/iltRegistrations/iltRegistrationKeys.ts'

// ilt is a domain specific abbreviation for "Instructor Led Training"
export function useIltRegistrationsInfinite(
  limit: MaybeRef<number>,
  courseId: MaybeRef<string>,
  status: MaybeRef<RegistrationStatus[]>,
  search: MaybeRef<string>
) {
  const query = useInfiniteQuery({
    queryKey: [
      QUERY_KEY_ILT_REGISTRATIONS,
      { limit, courseId, status, search },
    ],
    queryFn: ({ pageParam }) =>
      graphqlRequest<EventRegistrationsQuery>(EventRegistrationsDocument, {
        page: pageParam,
        courseId: unref(courseId),
        limit: unref(limit),
        filter: {
          status: unref(status),
          search: unref(search),
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pageInfo = lastPage?.iltCourseRegistrations.pageInfo
      return pageInfo?.hasNextPage ? pageInfo.nextPage : undefined
    },
    enabled: unref(courseId) !== undefined || unref(courseId) !== '',
  })

  // We flatten the paginated results from useInfiniteQuery into a single array
  const registrations = computed(() => {
    if (!query.data.value?.pages) return []
    return query.data.value.pages.flatMap(
      (page) => page.iltCourseRegistrations.nodes
    )
  })

  return {
    ...query,
    registrations,
  }
}
